import {CheckIcon, Loader2Icon, XIcon} from "lucide-react";
import {useState} from "react";

import {Badge} from "@components/ui/badge";
import {Button} from "@components/ui/button";

import type {SandboxInsight} from "@hooks/use-vnext";

interface SandboxInsightCardProps {
    insight: SandboxInsight;
    onPromote: (insightId: string) => void;
    onDismiss: (insightId: string) => Promise<void>;
}

const TYPE_ICONS: Record<string, string> = {
    finding: "\u{1F4CA}",
    anomaly: "\u26A0\uFE0F",
    trend: "\u{1F4C8}",
    recommendation: "\u{1F4A1}",
};

function confidenceColor(c: number): string {
    if (c >= 0.8) return "text-green-700 bg-green-50";
    if (c >= 0.6) return "text-amber-700 bg-amber-50";
    return "text-gray-600 bg-gray-50";
}

export function SandboxInsightCard({insight, onPromote, onDismiss}: SandboxInsightCardProps) {
    const [dismissing, setDismissing] = useState(false);
    const icon = TYPE_ICONS[insight.insight_type] || "\u{1F4CA}";
    const isActionable = insight.status === "draft" || insight.status === "reviewed";

    const handleDismiss = async () => {
        setDismissing(true);
        try {
            await onDismiss(insight.id);
        } finally {
            setDismissing(false);
        }
    };

    return (
        <div className="flex items-start justify-between gap-3 rounded-lg border p-3">
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm">{icon}</span>
                    <span className="text-sm font-medium text-gray-900 truncate">
                        {insight.title}
                    </span>
                    {insight.status === "promoted" && (
                        <Badge variant="secondary" className="bg-green-100 text-green-800 text-[10px]">
                            Promoted
                        </Badge>
                    )}
                    {insight.status === "dismissed" && (
                        <Badge variant="outline" className="text-gray-400 text-[10px]">
                            Dismissed
                        </Badge>
                    )}
                </div>
                {insight.summary && (
                    <p className="text-xs text-gray-600 mb-1 line-clamp-2">{insight.summary}</p>
                )}
                <div className="flex items-center gap-2 text-[10px]">
                    <span className={`px-1.5 py-0.5 rounded ${confidenceColor(insight.confidence)}`}>
                        Confidence: {Math.round(insight.confidence * 100)}%
                    </span>
                    {insight.source_excerpt && (
                        <span className="text-gray-400 truncate max-w-[200px]">
                            {insight.source_excerpt}
                        </span>
                    )}
                </div>
            </div>
            {isActionable && (
                <div className="flex items-center gap-1 shrink-0">
                    {insight.proposed_op_json &&
                        Object.keys(insight.proposed_op_json).length > 0 && (
                            <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs border-[#5BF6C8] text-gray-700 hover:bg-[#5BF6C8]/10"
                                onClick={() => onPromote(insight.id)}
                            >
                                <CheckIcon className="h-3 w-3 mr-1" />
                                Promote
                            </Button>
                        )}
                    <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-gray-400 hover:text-red-500"
                        onClick={handleDismiss}
                        disabled={dismissing}
                    >
                        {dismissing ? (
                            <Loader2Icon className="h-3 w-3 animate-spin" />
                        ) : (
                            <XIcon className="h-3 w-3" />
                        )}
                    </Button>
                </div>
            )}
        </div>
    );
}
